import React, { useState, useEffect, useRef } from "react";
import styles from "./checklist.module.css";
import CheckboxField from "../CustomFields/CheckboxField";
import SelectField from "../CustomFields/SelectField";
import MultiselectField from "../CustomFields/MultiselectField";
import ChecklistContextMenu from "./ChecklistContextMenu";
import { Progress, Flex, Collapse, Tooltip, notification, Form } from "antd";
import { ExclamationOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { useChecklistsState } from "../../state/checklistsState";
import { toggleCustomField, selectCustomFieldValue, multiselectCustomFieldValues, updateHiddenChecklists } from "../../services/checklistService";
import { registerChecklistBlocking, clearChecklistBlocking } from "../../services/pipelineService";
import { isChecklistCollapsedByDefault } from "../../utils/checklistVisualSettings";
import { restoreAmoField, syncAmoFieldMount } from "../../services/amoFieldMountService";

const CHECKLIST_PANEL_KEY = "1";
const AMO_FIELD_SYNC_DELAYS = [0, 80, 250, 600];

const getChecklistActiveKeys = (visualSettings) =>
    isChecklistCollapsedByDefault(visualSettings) ? [] : [CHECKLIST_PANEL_KEY];

const getAmoFieldId = (field) => String(field?.fieldId || "");

const isAmoFieldEntered = (fieldId) => {
    try {
        if (!fieldId) {
            return false;
        }

        const attributes = AMOCRM.data.current_card?.model?.attributes;
        const fieldValue = attributes?.[`CFV[${fieldId}]`];
        const amoFields = AMOCRM.constant("account")?.cf || {};
        const amoField = amoFields[fieldId];
        const isMultiselect = amoField?.TYPE_CODE === 'MULTISELECT' || Number(amoField?.TYPE_ID) === 4;

        if (isMultiselect) {
            if (Array.isArray(fieldValue) && fieldValue.length > 0) {
                return true;
            }

            if (attributes && amoField?.ENUMS) {
                return Object.keys(amoField.ENUMS).some(enumId => {
                    const enumKey = `CFV[${fieldId}][${enumId}]`;
                    const enumValue = attributes[enumKey];
                    return enumValue && enumValue !== "";
                });
            }

            return false;
        }

        if (Array.isArray(fieldValue)) {
            return fieldValue.length > 0;
        }

        return Boolean(fieldValue) && String(fieldValue).trim() !== '';
    } catch (error) {
        return false;
    }
};

const AmoFieldMount = ({ field }) => {
    const containerRef = useRef(null);
    const fieldId = getAmoFieldId(field);
    const entered = isAmoFieldEntered(fieldId);

    useEffect(() => {
        if (!fieldId || !containerRef.current) {
            return undefined;
        }

        const syncField = () => {
            syncAmoFieldMount(fieldId, containerRef.current);
        };

        const syncTimers = new Set();

        const scheduleSyncField = () => {
            AMO_FIELD_SYNC_DELAYS.forEach((delayMs) => {
                const timerId = window.setTimeout(() => {
                    syncTimers.delete(timerId);
                    syncField();
                }, delayMs);
                syncTimers.add(timerId);
            });
        };

        scheduleSyncField();
        document.addEventListener("click", scheduleSyncField, true);

        return () => {
            document.removeEventListener("click", scheduleSyncField, true);
            syncTimers.forEach((timerId) => window.clearTimeout(timerId));
            syncTimers.clear();
            restoreAmoField(fieldId);
        };
    }, [fieldId]);

    return (
        <Form.Item>
            <Flex gap={10} width="100%">
                {!entered && (
                    <Tooltip title="Заполните поле">
                        <ExclamationOutlined style={{ color: 'red' }} />
                    </Tooltip>
                )}
                <div
                    ref={containerRef}
                    className={!entered ? styles.invalidField : ''}
                    style={{ flex: 1 }}
                />
            </Flex>
        </Form.Item>
    );
};

const ChecklistContent = ({ 
    checklist, 
    updatingFields, 
    shouldDisableField,
    getFieldBlockingReason,
    checklists
}) => {
    const customFields = checklist.custom_fields;

    return (
        <>
            {/* Отображение пользовательских полей */}
            {customFields.map(cf => {
                const isDisabled = shouldDisableField(checklist.id, cf.id) || updatingFields[cf.id];
                const disabledReason = getFieldBlockingReason(checklist.id, cf.id);
                if (cf.type === "C") { 
                    let checked = false;
                    if (cf.content) {
                        checked = cf.content.active;
                    }
                    return (
                        <Tooltip key={cf.id} title={disabledReason || ""}>
                            <div>
                                <CheckboxField
                                    customFieldId={cf.id}
                                    initialChecked={checked}
                                    label={cf.name}
                                    onToggle={(toggle) => toggleCustomField(checklist.id, cf, toggle)}
                                    disabled={isDisabled}
                                />
                            </div>
                        </Tooltip>
                    );
                } else if (cf.type === "S") {
                    const value = cf.content?.value || '';
                    return (
                        <Tooltip key={cf.id} title={disabledReason || ""}>
                            <div>
                                <SelectField
                                    field={cf}
                                    value={value}
                                    onChange={(newValue) => selectCustomFieldValue(checklist.id, cf, newValue)}
                                    disabled={isDisabled}
                                />
                            </div>
                        </Tooltip>
                    );
                } else if (cf.type === "M") {
                    const values = cf.content?.values || [];
                    return (
                        <Tooltip key={cf.id} title={disabledReason || ""}>
                            <div>
                                <MultiselectField
                                    field={cf}
                                    values={values}
                                    onChange={(newValues) => multiselectCustomFieldValues(checklist.id, cf, newValues)}
                                    disabled={isDisabled}
                                />
                            </div>
                        </Tooltip>
                    );
                }
                return null;
            })}

            {/* Амовские поля */}
            {checklist.fields.map((field, index) => {
                return <AmoFieldMount key={field.fieldId || index} field={field} />;
            })}

            {/* Дочерние чеклисты */}
            {checklists.filter(c => c.parent === checklist.id).sort((a, b) => a.position - b.position).map((child) => (
                <div key={child.id} className={styles.subItem}>
                    <Checklist checklist={child} isNested={true} />
                </div>
            ))}
        </>
    );
};


let initialPipelineSelectStyles = null;

const Checklist = ({ checklist, isNested = false }) => {
    const [forceUpdate, setForceUpdate] = useState(false);
    const [updatingFields, setUpdatingFields] = useState({});
    const [activeKeys, setActiveKeys] = useState(() =>
        getChecklistActiveKeys(checklist.visual_settings)
    );
    
    const { 
        checklists, 
        hiddenChecklists, 
        updateCustomField,
        getPercentOfSelectedCFs,
        setHiddenChecklists,
        shouldDisableField,
        getFieldBlockingReason
    } = useChecklistsState();

    const customFields = checklist.custom_fields;
    const percentOfSelectedCFs = getPercentOfSelectedCFs(checklist.id);
    const isConteinerShow = !hiddenChecklists.includes(checklist.id);

    
    useEffect(() => {
        // Каждый обязательный чеклист, независимо от вложенности,
        // регистрируется в централизованной системе блокировки.
        registerChecklistBlocking(
            checklist.id,
            percentOfSelectedCFs,
            checklist.custom_fields_required,
            isConteinerShow,
            checklist.name
        );
    }, [checklist.id, percentOfSelectedCFs, checklist.custom_fields_required, isConteinerShow, checklist.name, checklist.parent]);

    useEffect(() => {
        const handleSaveButtonClick = () => {
            setForceUpdate(prev => !prev);
        };

        const handleAmoModelUpdate = () => {
            setForceUpdate(prev => !prev);
        };

        const observer = new MutationObserver((mutationsList) => {
            for (const mutation of mutationsList) {
                if (mutation.type === 'childList') {
                    const saveButton = document.querySelector('.card-top-save-button');
                    if (saveButton) {
                        saveButton.addEventListener('click', handleSaveButtonClick);
                    }
                }
            }
        });

        observer.observe(document.body, { childList: true, subtree: true });

        window.addEventListener('amoModelUpdated', handleAmoModelUpdate);

        return () => {
            observer.disconnect();
            const saveButton = document.querySelector('.card-top-save-button');
            if (saveButton) {
                saveButton.removeEventListener('click', handleSaveButtonClick);
            }
            window.removeEventListener('amoModelUpdated', handleAmoModelUpdate);
        };
    }, []);

    useEffect(() => {
        setActiveKeys(getChecklistActiveKeys(checklist.visual_settings));
    }, [checklist.id, checklist.visual_settings?.collapsed_by_default]);
    
    const shouldShowChecklist = (checklistToCheck) => {
        
        if (hiddenChecklists.includes(checklistToCheck.id)) {
            return false;
        }
        
        const hasCustomFields = checklistToCheck.custom_fields && checklistToCheck.custom_fields.length > 0;
        const hasAmoFields = checklistToCheck.fields && checklistToCheck.fields.length > 0;
        
        const hasFilledCustomFields = checklistToCheck.custom_fields && checklistToCheck.custom_fields.some(cf => {
            if (cf.type === "C") {
                return cf.content && cf.content.active;
            } else if (cf.type === "S") {
                return cf.content && cf.content.value;
            } else if (cf.type === "M") {
                return cf.content && cf.content.values && cf.content.values.length > 0;
            }
            return false;
        });
        
        const hasFilledAmoFields = checklistToCheck.fields && checklistToCheck.fields.some(field => {
            return isAmoFieldEntered(getAmoFieldId(field));
        });
        
        const childChecklists = checklists.filter(c => c.parent === checklistToCheck.id);
        const hasVisibleChildren = childChecklists.some(child => shouldShowChecklist(child));
        
        return hasCustomFields || hasAmoFields || hasFilledCustomFields || hasFilledAmoFields || hasVisibleChildren;
    };
    
    
    if (!shouldShowChecklist(checklist)) {
        return null;
    }

    const setFieldUpdating = (fieldId, isUpdating) => {
        setUpdatingFields(prev => ({ ...prev, [fieldId]: isUpdating }));
    };

    const content = (
        <ChecklistContent 
            checklist={checklist}
            updatingFields={updatingFields}
            shouldDisableField={shouldDisableField}
            getFieldBlockingReason={getFieldBlockingReason}
            checklists={checklists}
        />
    );

    return (
        <ChecklistContextMenu checklist={checklist}>
            <Collapse
                style={{ backgroundColor: 'transparent' }}
                bordered={false}
                activeKey={activeKeys}
                onChange={(keys) => {
                    if (Array.isArray(keys)) {
                        setActiveKeys(keys);
                        return;
                    }
                    setActiveKeys(keys ? [keys] : []);
                }}
                items={[
                    {
                        key: CHECKLIST_PANEL_KEY,
                        label: (
                            <Flex gap={10} width="100%" align="center">
                                <div style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '16px', display: 'flex', alignItems: 'center' }}>
                                    {checklist.name}
                                    {checklist.custom_fields_required && percentOfSelectedCFs < 100 && (
                                        <Tooltip title="Нужно заполнить все поля чеклистов">
                                            <ExclamationCircleOutlined style={{ color: 'red', marginLeft: '8px' }} />
                                        </Tooltip>
                                    )}
                                </div>
                                {percentOfSelectedCFs >= 0 && (
                                    <div style={{ width: '300px', minWidth: '100px' }}>
                                        <Progress percent={percentOfSelectedCFs} />
                                    </div>
                                )}
                            </Flex>
                        ),
                        children: isNested ? content : <Form layout="vertical">{content}</Form>,
                    },
                ]}
            />
        </ChecklistContextMenu>
    );
};

export default Checklist;
